import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import MuiLink from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useAuth } from '../../services/auth/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

import SignInLayout from './SignInLayout';
import OverlayLoader from '../../components/overlayLoader';

// Store
import otpService from '../../services/api/otp';

import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';

export default function ResellerSignIn(props) {
  const { login, setIsAuthenticated } = useAuth();
  let navigate = useNavigate();
  const { t } = useTranslation();
  const [signInRole, setSignInRole] = useState('reseller');

  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState(false);
  const [otpError, setOtpError] = useState(false);
  const [otpRequested, setOtpRequested] = useState(false);
  const [otp, setOtp] = useState('');
  const [phoneErrorMessage, setPhoneErrorMessage] = useState('');
  const [otpErrorMessage, setOtpErrorMessage] = useState('');
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState(false);
  const [snackSeverity, setSnackSeverity] = useState("");
  const [loading, setLoading] = useState(false);
  const [showReferredByInput, setShowReferredByInput] = useState(false);
  const [referredBy, setReferredBy] = useState('');

  const validatePhone = () => {
    if (!phone || !/^\d{10}$/.test(phone)) {
      setPhoneError(true);
      setPhoneErrorMessage('phone_number.errors.invalid');
      return false;
    } else {
      setPhoneError(false);
      setPhoneErrorMessage('');
      return true;
    }
  };

  const validateOtp = () => {
    if (!otp || otp.length !== 6) {
      setOtpError(true);
      setOtpErrorMessage('otp.errors.length');
      return false;
    } else {
      setOtpError(false);
      setOtpErrorMessage('');
      return true;
    }
  };

  const handleClose = () => {
    setShowSnackbar(false);
    setSnackBarMessage("");
    setSnackSeverity("");
  };

  const requestOtp = (event) => {
    event.preventDefault();
    if (validatePhone()) {
      setLoading(true);
      // Trigger OTP request logic here (e.g., send request to backend)
      otpService.sendOtp(phone, signInRole).then((response) => {
        if (response.is_new_connector) {
          setShowReferredByInput(true);
        }
        if (response.is_rm){
          setSignInRole('rm');
        }
        setOtpRequested(true);
        setShowSnackbar(true);
        setSnackBarMessage('otp.request_success');
        setSnackSeverity("success");
      })
        .catch((error) => {
          let message = null;
          if (error instanceof AxiosError) {
            message = error.response?.data.error ?? error.message;
          } else if (error instanceof Error) {
            message = error.message;
          }
          setPhoneError(true);
          setPhoneErrorMessage(message || 'Something went wrong!');
          setSnackSeverity("error");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (validatePhone() && validateOtp()) {
      const data = new FormData(event.currentTarget);
      otpService.verifyOtp(data.get('phone'), data.get('otp'), signInRole, referredBy, navigate, setIsAuthenticated)
        .then((response) => {
          if (response) {
            // Assuming response contains the verifyToken structure
            const { access_token, refresh_token } = response;
            login(access_token, refresh_token);
            navigate('/dashboard', { replace: true });
          } else {
            setShowSnackbar(true);
            setSnackBarMessage('errors.something_went_wrong');
            setSnackSeverity("error");
          }

        })
        .catch((error) => {
          // Handle incorrect OTP case
          setOtpError(true);
          var message = error.response.data.detail ?? error.response.data.error;
          if (!message) {
            message = 'otp.errors.incorrect';
          }
          setShowSnackbar(true);
          setSnackBarMessage(message);
          setSnackSeverity("error");
          // setOtpErrorMessage(message);
        });
    }
  };

  return (
      <>
        <Snackbar
          open={showSnackbar}
          autoHideDuration={3000}
          onClose={handleClose}
        >
          <Alert
            onClose={handleClose}
            severity={snackSeverity}
            sx={{ width: '100%' }}
          >
            <Typography>{t(snackBarMessage)}</Typography>
          </Alert>
        </Snackbar>
        {loading && <OverlayLoader open={loading} text="" />}
        <SignInLayout>
                  <Box>
                    <Typography
                      component="h1"
                      variant="h5"
                      fontWeight={600}
                      color="primary"
                    >
                      {t('sign_in.header')}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      mt={0.5}
                    >
                      {signInRole === 'reseller' ? 'Reseller account' : 'Connector account'}
                    </Typography>
                  </Box>
                  <Box
                    component="form"
                    onSubmit={(e) => {
                      if (otpRequested) {
                        handleSubmit(e);
                      } else {
                        e.preventDefault(); // Prevent form submission when OTP is not requested
                      }
                    }}
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !otpRequested) {
                        requestOtp(e); // Trigger OTP request
                      }
                    }}
                  >
                    <FormControl>
                      <TextField
                        label={t('phone_number.label')}
                        required
                        fullWidth
                        id="phone"
                        type="number"
                        placeholder="Enter your phone number"
                        name="phone"
                        autoComplete="tel"
                        variant="outlined"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        error={phoneError}
                        helperText={t(phoneErrorMessage)}
                        sx={{
                          "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                            display: "none",
                          },
                          "& input[type=number]": {
                            MozAppearance: "textfield",   // Remove spinner for Firefox
                          },
                        }}
                      />
                    </FormControl>
                    {otpRequested && (
                      <FormControl>
                        <TextField
                          label={t('otp.label')}
                          required
                          fullWidth
                          name="otp"
                          placeholder={t('otp.enter')}
                          type="text"
                          id="otp"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value)}
                          autoComplete="one-time-code"
                          variant="outlined"
                          error={otpError}
                          helperText={t(otpErrorMessage)}
                        />
                      </FormControl>
                    )}
                    {signInRole === 'connector' && showReferredByInput && (
                      <FormControl>
                        <TextField
                          label={t('Referral Code')}
                          id="referred_by"
                          name="referred_by"
                          variant="outlined"
                          type="text"
                          placeholder={t('Referral Code')}
                          value={referredBy}
                          onChange={(e) => setReferredBy(e.target.value)}
                          required
                          fullWidth
                        />
                      </FormControl>
                    )}
                    {!otpRequested ? (
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={requestOtp}
                      >
                        {t('otp.request')}
                      </Button>
                    ) : (
                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                      >
                        {t('verify')}
                      </Button>
                    )}
                    <MuiLink
                      to={'/partner-sign-in'}
                      sx={(theme) => ({
                        alignSelf: 'center',
                        color: theme.palette.text.secondary,
                        fontSize: '0.875rem',
                        textDecoration: 'none',
                        cursor: 'pointer',
                        '&:hover': {
                          color: theme.palette.primary.main,
                          textDecoration: 'underline',
                        },
                      })}
                      component={Link}
                    >
                      {t('sign_in.not_a_reseller')}
                    </MuiLink>
                    <MuiLink
                      to={'/sign-in'}
                      sx={(theme) => ({
                        alignSelf: 'center',
                        color: theme.palette.text.secondary,
                        fontSize: '0.875rem',
                        textDecoration: 'none',
                        cursor: 'pointer',
                        '&:hover': {
                          color: theme.palette.primary.main,
                          textDecoration: 'underline',
                        },
                      })}
                      component={Link}
                    >
                      Sign in as a Connector
                    </MuiLink>
                  </Box>
                        </SignInLayout>
  
      </>
    );
}
